import { useFormikContext } from 'formik';
import moment from 'moment';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import TruckIconOutlined from 'components/icons/TruckIconOutlined';
import useCountries from 'hooks/useCountries';
import { ETA_FORMAT } from 'views/shipments/create-shipment/utils';
import { NewShipmentFormFields, Steps } from '../../../types';
import EtaDatePicker from '../EtaDatePicker';
import { StyledFormSectionContentWrapper, StyledReadonlySectionWrapper } from '../../../styles';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import CountrySelect from 'components/new-ui/composed/selects/CountrySelect';

const ShipmentDetails = () => {
    const { getFieldProps, getFieldMeta, getFieldHelpers, values } = useFormikContext<NewShipmentFormFields>();
    const { isIdle, isActive, isComplete, currentStepConfig, resetStep } =
        useMultiStepFormContext<NewShipmentFormFields>();
    const { countries } = useCountries();

    const stepKey = Steps.SHIPMENT_DETAILS;
    const disabled = currentStepConfig?.disabled ?? false;

    const handleReset = (fieldKey: string) => {
        resetStep(stepKey, fieldKey);
    };

    const selectedOriginCountry =
        countries.list.find((c) => c.countryCode === values.shipmentDetails.countryOriginCode)?.name ?? '';
    const selectedEntryCountry =
        countries.list.find((c) => c.countryCode === values.shipmentDetails.entryCountryCode)?.name ?? '';
    const selectedDestinationCountry =
        countries.list.find((c) => c.countryCode === values.shipmentDetails.destinationCode)?.name ?? '';
    const formattedETA = values.shipmentDetails.eta ? moment(values.shipmentDetails.eta).format(ETA_FORMAT) : '';

    if (isIdle(Steps.SHIPMENT_DETAILS)) {
        return <FormSectionHeader title="Shipment Details" icon={<TruckIconOutlined />} />;
    }

    if (isComplete(Steps.SHIPMENT_DETAILS)) {
        return (
            <StyledReadonlySectionWrapper>
                <FormSectionHeader title="Shipment Details" icon={<TruckIconOutlined />} />
                <ReadonlyFormItem
                    disabled={disabled}
                    label="Carrier"
                    value={values.shipmentDetails.carrier}
                    onReset={() => handleReset('carrier')}
                />
                <ReadonlyFormItem
                    disabled={disabled}
                    label="Country Origin"
                    value={selectedOriginCountry}
                    onReset={() => handleReset('countryOriginCode')}
                />

                <ReadonlyFormItem
                    disabled={disabled}
                    label="Entry Country"
                    value={selectedEntryCountry}
                    onReset={() => handleReset('entryCountryCode')}
                />
                <ReadonlyFormItem
                    disabled={disabled}
                    label="Destination"
                    value={selectedDestinationCountry}
                    onReset={() => handleReset('destinationCode')}
                />
                <ReadonlyFormItem
                    disabled={disabled}
                    label="ETA"
                    value={formattedETA}
                    onReset={() => handleReset('eta')}
                />
            </StyledReadonlySectionWrapper>
        );
    }

    if (isActive(Steps.SHIPMENT_DETAILS)) {
        return (
            <StyledFormSectionContentWrapper>
                <FormSectionHeader
                    title="Shipment Details"
                    icon={<TruckIconOutlined />}
                    subtitle="Please provide the following details for this bulk submission"
                />
                <FormInput
                    disabled={disabled}
                    required={true}
                    label="Carrier:"
                    placeholder="Carrier"
                    fieldProps={getFieldProps('shipmentDetails.carrier')}
                    fieldMeta={getFieldMeta('shipmentDetails.carrier')}
                    fieldHelper={getFieldHelpers('shipmentDetails.carrier')}
                />

                <CountrySelect
                    label="Country Origin:"
                    placeholder="Country origin"
                    required={true}
                    fieldProps={getFieldProps('shipmentDetails.countryOriginCode')}
                    fieldMeta={getFieldMeta('shipmentDetails.countryOriginCode')}
                    fieldHelper={getFieldHelpers('shipmentDetails.countryOriginCode')}
                    disabled={disabled}
                />

                <CountrySelect
                    label="Entry Country:"
                    placeholder="Entry country"
                    required={true}
                    fieldProps={getFieldProps('shipmentDetails.entryCountryCode')}
                    fieldMeta={getFieldMeta('shipmentDetails.entryCountryCode')}
                    fieldHelper={getFieldHelpers('shipmentDetails.entryCountryCode')}
                    disabled={disabled}
                />

                <CountrySelect
                    label="Destination:"
                    placeholder="Destination"
                    fieldProps={getFieldProps('shipmentDetails.destinationCode')}
                    fieldMeta={getFieldMeta('shipmentDetails.destinationCode')}
                    fieldHelper={getFieldHelpers('shipmentDetails.destinationCode')}
                    required={true}
                    disabled={disabled}
                />

                <div>
                    <EtaDatePicker />
                </div>
            </StyledFormSectionContentWrapper>
        );
    }

    return null;
};

export default ShipmentDetails;
